/**
 * THE VOCABULARY OF THE OPEN LEGACY-RETIREMENT CONTRACTS (ADR 0012 "Open 4c contracts"; ADR 0013, PROPOSED). Names only.
 *
 * WHAT THIS IS. ADR 0012 lists eleven contracts that must be decided before any part of the legacy payroll path is retired (4c-1: the
 * legacy calculation, the comparator, the shadow runner, legacy-only lock capture; 4c-2: the stored statutory rule table and the
 * settings write path). None is decided. This file gives each one a stable identifier and NAMES its candidate options, so the code
 * that encodes today's behaviour can say which option it implements, and a replacement can be added later without renaming anything.
 *
 * WHAT THIS IS NOT. It decides nothing:
 *
 *   - exactly ONE option per contract is `current`: what the code does today. Selecting it is a no-op.
 *   - every other option is `named_not_selectable`. It exists so the alternatives are written down and discussed, not so anyone can
 *     switch to one. Anything that is asked to use a not-selectable option FAILS CLOSED (`assertSelectableOption` throws); it never
 *     falls back to the current option, because a silent fallback would be a decision made by accident.
 *   - a summary is a description of the option, not a recommendation. Recommendations are in ADR 0013 and are the owner's to accept.
 *
 * The helpers at the end (`selectableOptionsOf`, `assertSelectableOption`) are what makes "fail closed" one mechanism rather than eleven.
 *
 * Nothing here is on the wire. No route, request or response carries these types; adding one is a contract change with its own PR
 * (CLAUDE.md invariant 8). The file is pure and RN-safe.
 */

import { AppError } from '../errors.js';

/** The eleven open contracts, in the order ADR 0012 lists them. */
export const RETIREMENT_CONTRACT_IDS = [
  'entry_evidence', // 1  replacement evidence for the comparator and the engine entry gate
  'unmapped_output_meaning', // 2  what an `unmapped` engine output means once the comparator is gone
  'mode_semantics', // 3  mode semantics after retirement, incl. what a missing rollout row means
  'rollback_target', // 4  where a rollback goes
  'rollback_effect_on_reviewed_version', // 5  whether a rollback invalidates a review-ready or submitted version
  'legacy_run_retention', // 6  retention of historic legacy runs and their evidence
  'legacy_replay_policy', // 7  replay policy for legacy 1.0.0 runs
  'legacy_finalized_reopen', // 8  whether a legacy-basis finalized run may be reopened
  'declaration_catalogue_owner', // 9  ownership of the declaration catalogue and windows
  'retirement_scope', // 10 the exact scope and entity count
  'route_removal', // 11 contract-first treatment for deleting routes
] as const;
export type RetirementContractId = (typeof RETIREMENT_CONTRACT_IDS)[number];

/** The number ADR 0012 gives each contract in its "Open 4c contracts" list. */
export const RETIREMENT_CONTRACT_NUMBER: Readonly<Record<RetirementContractId, number>> = {
  entry_evidence: 1,
  unmapped_output_meaning: 2,
  mode_semantics: 3,
  rollback_target: 4,
  rollback_effect_on_reviewed_version: 5,
  legacy_run_retention: 6,
  legacy_replay_policy: 7,
  legacy_finalized_reopen: 8,
  declaration_catalogue_owner: 9,
  retirement_scope: 10,
  route_removal: 11,
};

/**
 * `current`: what the code does today, and the only option that may be selected. `named_not_selectable`: an alternative that is
 * written down and nothing more; asking for it fails closed.
 */
export type RetirementOptionStatus = 'current' | 'named_not_selectable';

export interface RetirementOption {
  readonly status: RetirementOptionStatus;
  /** What the option means, in one sentence. A description, never a recommendation. */
  readonly summary: string;
}

const current = (summary: string): RetirementOption => ({ status: 'current', summary });
const named = (summary: string): RetirementOption => ({ status: 'named_not_selectable', summary });

/** Every contract's options, keyed by option id. Exactly one `current` per contract (a test holds this). */
export const RETIREMENT_OPTIONS = {
  // 1
  entry_evidence: {
    legacy_comparator: current(
      'Engine entry needs a completed shadow comparison against the legacy calculation whose gate passed.',
    ),
    golden_fixtures: named(
      'Engine entry needs a passing set of reviewed golden fixtures for the entity’s rule composition; no legacy calculation runs.',
    ),
    optional_per_country_comparator: named(
      'A country pack may declare a comparator; an entity of a country without one is admitted on other evidence.',
    ),
  },
  // 2
  unmapped_output_meaning: {
    comparator_reported: current(
      'An unmapped engine output is whatever the latest comparison recorded for it, and raises a readiness blocker.',
    ),
    static_mapping_table: named(
      'An unmapped output is one the static engine-to-component mapping table marks unmapped, independent of any run.',
    ),
    pack_declared_output_targets: named(
      'The country pack declares where every output lands (a component, or explicitly nowhere); an undeclared output is a pack defect.',
    ),
  },
  // 3 — what a MISSING rollout row means (the option ids are the policy)
  mode_semantics: {
    legacy: current('No rollout row means the entity is in legacy mode.'),
    shadow: named('No rollout row means the entity is in shadow mode.'),
    engine: named('No rollout row means the entity is in engine mode.'),
    refuse: named('No rollout row is an error: every entity must have an explicit mode.'),
  },
  // 4
  rollback_target: {
    caller_chooses_shadow_or_legacy: current(
      'Leaving engine mode goes to whichever of shadow or legacy the caller names.',
    ),
    always_shadow: named('Leaving engine mode always goes to shadow.'),
    previous_engine_rules: named(
      'Leaving engine mode keeps the engine and returns the entity to its previous published rules and configuration.',
    ),
    hold_no_calculation: named(
      'Leaving engine mode holds the entity: nothing calculates until a mode is chosen again.',
    ),
  },
  // 5
  rollback_effect_on_reviewed_version: {
    none_until_relock: current(
      'A rollback invalidates nothing: a review-ready or submitted version stays as it is; only a new calculation is refused until the inputs are locked again.',
    ),
    invalidate_review_ready: named('A rollback supersedes a review-ready version.'),
    invalidate_review_ready_and_submitted: named(
      'A rollback supersedes a review-ready version and returns a submitted run to changes requested.',
    ),
    refuse_rollback_while_reviewed: named(
      'A rollback is refused while any run of the entity holds a review-ready or submitted version.',
    ),
  },
  // 6
  legacy_run_retention: {
    none_defined_retained: current(
      'Nothing purges a legacy run or its evidence and no retention period is stated anywhere.',
    ),
    fixed_period_then_archive: named(
      'Legacy runs and evidence are kept for a stated period, then archived.',
    ),
    evidence_summary_only: named(
      'Legacy runs are kept; the per-employee comparison evidence is reduced to counts after a period.',
    ),
  },
  // 7
  legacy_replay_policy: {
    not_replayable_reported: current(
      'Verifying a version that is not an engine version reports it as not replayable, with its problems named, and changes nothing.',
    ),
    stored_results_checksum_only: named(
      'A legacy version is verified by re-hashing its stored results and inputs; nothing is recalculated.',
    ),
    frozen_legacy_module_retained: named(
      'The legacy arithmetic is kept, frozen and unreachable from payroll, solely to replay legacy versions.',
    ),
    evidence_only_no_replay: named(
      'A legacy version is evidence to read; replay is declared not to exist for it.',
    ),
  },
  // 8
  legacy_finalized_reopen: {
    allowed_recalculates_under_current_mode: current(
      'A legacy-basis finalized run may be reopened while no payment was initiated; the next lock and calculation follow the entity’s mode then.',
    ),
    refuse_reopen_of_legacy_basis: named(
      'A finalized run whose approved version was not calculated by the engine may not be reopened.',
    ),
    reopen_requires_explicit_basis_change: named(
      'Reopening a legacy-basis run is allowed only with an explicit, audited acknowledgement that the basis changes.',
    ),
  },
  // 9
  declaration_catalogue_owner: {
    payroll_tax_year_config: current(
      'The tax-year configuration table owns the declaration catalogue and windows, beside its legacy payroll role.',
    ),
    platform_rules: named(
      'Platform-reviewed rule data owns the declaration catalogue; the windows move elsewhere.',
    ),
    dedicated_declaration_catalogue: named(
      'A new, dedicated catalogue table owns categories, sections, windows and the salary-basis constants for the declaration workflow only.',
    ),
  },
  // 10
  retirement_scope: {
    undecided: current(
      'No scope is defined and legacy retirement is blocked: neither an entity count nor a set of configurations is stated.',
    ),
    single_pilot_entity: named('One representative entity completes one full cycle.'),
    all_engine_entities_one_cycle: named(
      'Every entity in engine mode completes one full cycle before its legacy path is retired.',
    ),
    every_configuration_combination: named(
      'Each distinct statutory and configuration combination needs its own evidence.',
    ),
  },
  // 11
  route_removal: {
    contract_pr_then_handler_pr: current(
      'CLAUDE.md invariant 8: the contract changes in its own PR first, the implementation follows. Deleting a route cannot follow this literally, because a handler with no contract does not typecheck.',
    ),
    deprecate_then_remove: named(
      'The route is kept and marked gone (a contract PR), the handler answers 410 (a second PR), then contract and handler are deleted together (a third).',
    ),
    atomic_removal: named(
      'The contract and the handler are deleted in one PR, as a named exception to invariant 8 for retirement only.',
    ),
  },
} as const satisfies Record<RetirementContractId, Readonly<Record<string, RetirementOption>>>;

/** The option ids of one contract. */
export type RetirementOptionId<C extends RetirementContractId> = Extract<
  keyof (typeof RETIREMENT_OPTIONS)[C],
  string
>;

// The named unions the code that encodes today's behaviour talks in.
export type EntryEvidenceSource = RetirementOptionId<'entry_evidence'>;
export type UnmappedOutputMeaning = RetirementOptionId<'unmapped_output_meaning'>;
/** What a missing rollout row means. `legacy` is today's rule; the others are named, not selectable. */
export type MissingRolloutRowPolicy = RetirementOptionId<'mode_semantics'>;
export type RollbackTargetPolicy = RetirementOptionId<'rollback_target'>;
export type RollbackEffectPolicy = RetirementOptionId<'rollback_effect_on_reviewed_version'>;
export type LegacyRetentionPolicy = RetirementOptionId<'legacy_run_retention'>;
export type LegacyReplayPolicy = RetirementOptionId<'legacy_replay_policy'>;
export type LegacyReopenPolicy = RetirementOptionId<'legacy_finalized_reopen'>;
export type DeclarationCatalogueOwner = RetirementOptionId<'declaration_catalogue_owner'>;
export type RetirementScopePolicy = RetirementOptionId<'retirement_scope'>;
export type RouteRemovalPolicy = RetirementOptionId<'route_removal'>;

// ---------------------------------------------------------------------------
// Failing closed
// ---------------------------------------------------------------------------

/** The option a contract is on today. Exactly one per contract; a test holds that. */
export function currentOptionOf<C extends RetirementContractId>(
  contract: C,
): RetirementOptionId<C> {
  const options = RETIREMENT_OPTIONS[contract] as Readonly<Record<string, RetirementOption>>;
  const found = Object.keys(options).find((id) => options[id]!.status === 'current');
  if (found === undefined)
    throw new Error(`Retirement contract ${contract} has no current option.`);
  return found as RetirementOptionId<C>;
}

/** The options of a contract that may be selected: today, only the current one. */
export function selectableOptionsOf<C extends RetirementContractId>(
  contract: C,
): readonly RetirementOptionId<C>[] {
  const options = RETIREMENT_OPTIONS[contract] as Readonly<Record<string, RetirementOption>>;
  return Object.keys(options).filter(
    (id) => options[id]!.status === 'current',
  ) as RetirementOptionId<C>[];
}

/** Asking for an option that is named but not selectable, or that does not exist. Never caught to fall back to the current one. */
export class UnselectableRetirementOptionError extends AppError {
  readonly contract: RetirementContractId;
  readonly option: string;
  constructor(contract: RetirementContractId, option: string, known: boolean) {
    super(
      'UNPROCESSABLE',
      422,
      known
        ? `Retirement contract "${contract}" has no decision recorded for option "${option}": it is named, not selectable.`
        : `Retirement contract "${contract}" has no option "${option}".`,
      { details: { code: 'retirement_option_not_selectable', contract, option, known } },
    );
    this.contract = contract;
    this.option = option;
  }
}

/**
 * FAIL CLOSED. Returns when `option` is selectable for `contract`; throws `UnselectableRetirementOptionError` otherwise. A caller
 * must not catch it and use the current option instead: a silent fallback is a decision made by accident.
 */
export function assertSelectableOption<C extends RetirementContractId>(
  contract: C,
  option: string,
): asserts option is RetirementOptionId<C> {
  const options = RETIREMENT_OPTIONS[contract] as Readonly<Record<string, RetirementOption>>;
  const found = options[option];
  if (found === undefined) throw new UnselectableRetirementOptionError(contract, option, false);
  if (found.status !== 'current')
    throw new UnselectableRetirementOptionError(contract, option, true);
}
